import isNumber from 'lodash/isNumber';
import pick from 'lodash/pick';
import StateController from 'panel/build/component-utils/state-controller';
import zipObject from 'lodash/zipObject';

export default class DashboardModelController extends StateController {
  constructor({model, selectorController, dashboardsClient}) {
    super(...arguments);
    this.parentController = selectorController;
    this.client = dashboardsClient;
    this._update(model);
  }

  get defaultState() {
    return {
      id: null,
      title: null,
      description: null,
      creator: null,
      creator_id: null,
      is_private: false,
      card_order: null,
      cards: null,
      is_restricted: false,
      can_update_basic: true,
      can_update_restricted: true,
      can_update_visibility: false,

      loaded: false,
      error: null,
    };
  }

  _updateCachedModel(results) {
    this._update(results);
    this.parentController._updateCachedDashboard(this.state.id, results);
  }

  getModel() {
    return pick(this.state, [
      `id`,
      `title`,
      `description`,
      `creator`,
      `creator_id`,
      `is_private`,
      `card_order`,
      `cards`,
      `is_restricted`,
      `can_update_basic`,
      `can_update_restricted`,
      `can_update_visibility`,
    ]);
  }

  getLoaded() {
    return this.state.loaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.loaded;
  }

  getError() {
    return this.state.error;
  }

  async loadModel() {
    this._update({loaded: false, error: null});
    // prevent redundant network requests
    if (!this._loadModelPromises) {
      this._loadModelPromises = this.client.querySingle(
        {
          projectID: this.parentController.projectID,
          dashboardID: this.state.id,
          workspaceId: this.parentController.workspaceId,
        },
        {clearCache: true},
      );
      Object.values(this._loadModelPromises).forEach((promise) =>
        promise
          .then((dashboard) => {
            dashboard && this._update({...dashboard, loaded: true, error: null});
          })
          .catch((error) => this._update({loaded: false, error})),
      );
    }

    // fallback to network if cached value doesn't exist
    (await this._loadModelPromises.cached) || (await this._loadModelPromises.network);

    Promise.all(Object.values(this._loadModelPromises))
      .catch((err) => {
        throw err;
      })
      .finally(() => (this._loadModelPromises = null));
  }

  async updateFields({fields = {}} = {}) {
    const originalModel = this.getModel();
    try {
      this._updateCachedModel({...this.getModel(), ...fields});

      const results = await this.client.patchUpdateDashboardFields({
        projectID: this.parentController.projectID,
        workspaceId: this.parentController.workspaceId,
        dashboardID: this.state.id,
        fields,
      });

      this._updateCachedModel(results);
    } catch (error) {
      this._updateCachedModel(originalModel);
      throw error;
    }
  }

  async updateCardFields({cardID, fields = {}}) {
    const {id, cards} = this.state;
    try {
      this._updateCachedModel({
        cards: cards.map((card) => (card.id === cardID ? {...card, ...fields} : card)),
      });

      const results = await this.client.patchUpdateDashboardCardFields({
        projectID: this.parentController.projectID,
        workspaceId: this.parentController.workspaceId,
        dashboardID: id,
        cardID,
        fields,
      });

      this._updateCachedModel({
        cards: cards.map((card) => (card.id === cardID ? {...card, ...results} : card)),
      });
    } catch (error) {
      this._updateCachedModel({cards});
      throw error;
    }
  }

  // eslint-disable-next-line camelcase
  async moveCard({card_id, to_index} = {}) {
    // eslint-disable-next-line camelcase
    if (!isNumber(card_id) || !isNumber(to_index)) {
      throw new Error(`invalid reorder arguments`);
    }

    const {id, card_order, cards} = this.state; // eslint-disable-line camelcase
    try {
      const cardMap = zipObject(
        cards.map((c) => c.id),
        cards,
      );
      const newCardOrder = card_order.filter((id) => id !== card_id); // eslint-disable-line camelcase
      newCardOrder.splice(to_index, 0, card_id);
      this._updateCachedModel({
        card_order: newCardOrder,
        cards: newCardOrder.filter((cardID) => cardID in cardMap).map((cardID) => cardMap[cardID]),
      });

      const updatedCardOrder = await this.client.postMoveDashboardCard({
        projectID: this.parentController.projectID,
        workspaceId: this.parentController.workspaceId,
        dashboardID: id,
        card_id, // eslint-disable-line camelcase
        to_index, // eslint-disable-line camelcase
      });

      this._updateCachedModel({
        card_order: updatedCardOrder,
        cards: updatedCardOrder.filter((cardID) => cardID in cardMap).map((cardID) => cardMap[cardID]),
      });
    } catch (error) {
      this._updateCachedModel({card_order, cards}); // eslint-disable-line camelcase
      throw error;
    }
  }

  async duplicateCard({cardID}) {
    const {card_order, cards} = this.state; // eslint-disable-line camelcase
    try {
      const dashboard = await this.client.postDuplicateDashboardCard({
        projectID: this.parentController.projectID,
        workspaceId: this.parentController.workspaceId,
        dashboardID: this.state.id,
        cardID,
      });
      this._updateCachedModel(dashboard);
    } catch (error) {
      this._updateCachedModel({card_order, cards}); // eslint-disable-line camelcase
      throw error;
    }
  }

  async removeCard({cardID}) {
    if (!isNumber(cardID)) {
      throw new Error(`invalid card ID`);
    }

    const {id, card_order, cards} = this.state; // eslint-disable-line camelcase
    try {
      this._updateCachedModel({
        card_order: card_order.filter((cid) => cid !== cardID), // eslint-disable-line camelcase
        cards: cards.filter((c) => c.id !== cardID),
      });

      await this.client.deleteDashboardCard({
        projectID: this.parentController.projectID,
        workspaceId: this.parentController.workspaceId,
        dashboardID: id,
        cardID,
      });
    } catch (error) {
      if (error.name !== `CardDoesNotExistError`) {
        this._updateCachedModel({card_order, cards}); // eslint-disable-line camelcase
      }
      throw error;
    }
  }

  async addBookmarks({bookmarkIDs}) {
    const dashboard = await this.client.postAddBookmarksToDashboard({
      projectID: this.parentController.projectID,
      workspaceId: this.parentController.workspaceId,
      dashboardID: this.state.id,
      bookmarkIDs,
    });
    this._updateCachedModel(dashboard);
  }
}
